// The bee-type sheet: an **inset popup over a live control stack** (design **D9**,
// `sandbox-screen-consolidation/design.md` §4.1, `client-sandbox-screen` "The bee-type sheet is an
// inset popup over a live control stack"; sketch `prototype/sketches/sandbox-screen-consolidation/
// type-modal.html`, geometry in the change's visual spec §6).
//
// **Why a popup and not a screen.** It floats with a 16px margin on every side and stops 228px
// short of the bottom — the 212px control stack plus that same margin — rounded on all four
// corners, `--shadow-sheet`, and **no scrim**. The field and its bees stay visible around every
// edge, and the playback bar, bee-type bar and Beekeeper input stay *live*: a run can be scrubbed
// while the code that produced it is read, and tapping another chip swaps this card's subject
// without closing it. That is the whole affordance — "a card opened on top of the game" rather
// than "the game went away" — so the absence of a scrim is load-bearing, not decorative, and for
// the same reason the card is deliberately **not** `aria-modal` and does **not** trap focus. Both
// are departures from `ChatOverlay`, whose full-bleed conversation genuinely is modal.
//
// **Geometry independence (design T7).** A `position: fixed` layer that measures no bar, sheet or
// map coordinate — no `getBoundingClientRect`, no `ResizeObserver`, no portal — inheriting the
// D-086/D-087 property `FirstRunGuide` relies on, so its content, tab switching and hand edit are
// provable in jsdom under zero layout. The inset and the radius are *style*, never measurement.
//
// **What the tabs may say.** Code renders the type's **verbatim slice of the colony document**
// (BPP-SPEC §15) — the bytes the Beekeeper's `edit_scripts` wrote, `#` comments included — never
// canonical source reprinted from an AST, and it degrades to the labelled whole-document repair
// surface when the working document does not parse, because no per-type slice is extractable then
// (`client-colony`, and the same `ui/colony/document-slice.ts` the live Colony screen slices with).
// Stats presents **only figures derivable from the loaded trace**: the count at the playhead and
// this type's share of the living colony, both off the resident detail record via
// `ui/rounds/type-counts.ts`. Deaths, nectar delivered and the population-across-the-run sparkline
// are design §6's gaps **G-A**/**G-C** and are omitted entirely — the sketch shows them populated,
// which is what a sketch is for. A note explains the absence; explaining an absence is not
// presenting a figure. Per-state flowchart counts are gap **G-B**; `BppProgramDiagram` renders the
// authoritative graph without inventing a tally and keeps a node-telemetry seam for later.
//
// **Hit targets.** Every control declares ≥ 44px (`--hit-target`, UI.md §4). The kit's `.mchip`
// (30) and `.tabs button` (40) from the sketch are deliberately raised here, the same deviation
// `Chip` (36 → 44) and `Sheet`'s close (36 → 44) already made. `CodeView`'s own Edit/Save/Cancel
// are 32/40px and are therefore *not* reused for the hand edit — raising them would change the
// live Colony screen's chrome, which this change must leave untouched (task 8.2) — so `CodeView`
// serves here only as the read-only highlighted view of the slice.

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { CodeView } from './CodeView';
import { BppProgramDiagram } from './BppProgramDiagram';
import { documentLines, sliceType } from '../../colony/document-slice';
import { countForType, type PlayheadPopulation } from '../../rounds/type-counts';

/** The 16px margin on every side (`inset: 16px 16px 228px`). Style, not a measurement. */
export const TYPE_SHEET_INSET_PX = 16;
/** The control stack's 212px plus the same margin: the bars are never underneath the card. */
export const TYPE_SHEET_STACK_CLEARANCE_PX = 228;

/** Rendered wherever a value has no source at the playhead — never a stale or invented one. */
const ABSENT = '—';
const ABSENT_CLOCK = '—:—';

/** Deaths, nectar delivered and the population series are gaps G-A/G-C. This names them and
 * carries no number of its own, so it can never be mistaken for a figure. Player-facing wording —
 * no "trace" — since a player reads this, not a developer. */
const STATS_GAP_NOTE =
  'More stats are on the way — deaths, nectar delivered and population over time will appear ' +
  'here in a future update.';

export type TypeSheetTab = 'code' | 'flowchart' | 'stats';

/** One compiler diagnostic against the working document, as the wire reports it. */
export interface TypeSheetDiagnostic {
  /** 1-indexed line in the whole document — what the degraded view marks. */
  line: number;
  message: string;
}

export interface TypeSheetProps {
  /** `ColonyTypeId` — what the playhead population is keyed on. */
  typeId: number;
  /** The type's own name, as it appears in the document's `type <name>` header. */
  typeName: string;
  /** Bee-type colour token; tints the flowchart cards and the header rule. */
  color?: string;
  /** Sprite src, as `BeeTypeBar` takes it. */
  sprite?: string;
  /** The buffed characteristic, e.g. `capacity`; omitted when unknown. */
  buff?: string | null;
  /** Buff multiplier (DESIGN §14 default ×2; per-characteristic overrides live in balance). */
  buffMultiplier?: number;
  /** This type is the colony's birth type. */
  birthType?: boolean;
  /** Offered only when the caller can act on it; omit for a read-only chip. */
  onSetBirthType?: () => void;
  /** The colony document's own bytes. Named `colonyDocument` rather than `document` so nothing in
   * this component can shadow the global of that name. */
  colonyDocument: string;
  /** `false` when the working document does not parse — the degraded whole-document mode. */
  documentValid?: boolean;
  /** Diagnostics against the working document; the first one's line is marked in that mode. */
  documentDiagnostics?: readonly TypeSheetDiagnostic[];
  /** The living, non-queen population at the playhead (`ui/rounds/type-counts.ts`). Its
   * unavailable value is a real state here, not an error: every readout derived from it renders
   * the placeholder. */
  population: PlayheadPopulation;
  /** `mm:ss` at the playhead, for the header census. */
  playheadLabel?: string;
  /**
   * Which tabs to offer, in order. Defaults to all three.
   *
   * `match-hud` **D-9**'s stated default is the only caller that narrows it: an **opponent's**
   * bee-type card opens with `['stats']`, because a count is board state while an authored script
   * is the player's own work — and opponent scripts are not on the wire at all, so Code and
   * Flowchart would have nothing honest to render. A single-entry list is also what makes the
   * restriction structural: the tab cannot be reached, rather than rendering an empty one.
   */
  tabs?: readonly TypeSheetTab[];
  /* `states` is deliberately absent (UI/UX audit F-01): the Flowchart tab derives its states from
   * `colonyDocument` inside this component, so there is one source of truth and a caller cannot
   * silently omit them — which is precisely how the tab shipped permanently empty. */
  /** A Beekeeper turn is in flight: the hand-edit affordance is withheld entirely, matching the
   * server's refusal of the same mutation. */
  turnInFlight?: boolean;
  /** Diagnostics from the last failed save, rendered beside the preserved draft. */
  editDiagnostics?: readonly string[];
  /** Commits this type's slice. Omit to offer no hand edit at all. */
  onSaveSlice?: (source: string) => void;
  /** Commits the whole document — the degraded mode's repair surface. */
  onSaveDocument?: (source: string) => void;
  onClose?: () => void;
  /**
   * The rounds G5 phase clock's bare-remainder mounting (`client-rounds-phase`,
   * `client-sandbox-screen`'s "the header of each inset popup" requirement) — rendered in this
   * header, beside the close control, while this sheet is the player's editing surface. A slot
   * rather than a hard dependency on `PhaseClock`: live mode's `Colony.tsx` opens this same sheet
   * and passes nothing, so nothing renders here for it.
   */
  headerRemainder?: ReactNode;
  /**
   * A full-width row under the buff/birth-type chips. Rounds mode passes nothing — its version
   * history lives on the playback bar's version selector, which is part of the control stack this
   * card floats over. The pvp Colony screen has no playback bar, so it mounts its existing
   * `VersionBar` (step older/newer + Activate, i.e. rollback) here, which is the nearest equivalent
   * position and keeps the affordance reachable with the sheet open.
   */
  toolbar?: ReactNode;
  style?: CSSProperties;
}

const TABS: readonly { id: TypeSheetTab; label: string }[] = [
  { id: 'code', label: 'Code' },
  { id: 'flowchart', label: 'Flowchart' },
  { id: 'stats', label: 'Stats' },
];

const CONTROL_BASE: CSSProperties = {
  minHeight: 'var(--hit-target)',
  padding: '0 16px',
  borderRadius: 'var(--r-pill)',
  fontFamily: 'var(--font-display)',
  fontWeight: 500,
  fontSize: 13,
  cursor: 'pointer',
};

function titleCase(name: string): string {
  return name.length === 0 ? name : name[0]!.toUpperCase() + name.slice(1);
}

export function TypeSheet({
  typeId,
  typeName,
  color = 'var(--accent)',
  sprite,
  buff,
  buffMultiplier = 2,
  birthType = false,
  onSetBirthType,
  colonyDocument,
  documentValid = true,
  documentDiagnostics,
  population,
  playheadLabel,
  tabs,
  turnInFlight = false,
  editDiagnostics,
  onSaveSlice,
  onSaveDocument,
  onClose,
  headerRemainder,
  toolbar,
  style,
}: TypeSheetProps) {
  const offeredTabs = TABS.filter((entry) => tabs === undefined || tabs.includes(entry.id));
  const [tab, setTab] = useState<TypeSheetTab>(offeredTabs[0]?.id ?? 'code');
  /**
   * **The selected tab, reconciled against what is currently on offer** (Bugbot round 2).
   *
   * `tab` is initialised once and deliberately survives a change of subject (see the effect
   * below — a player comparing two types' code should not be dropped back to Code each tap). But
   * `tabs` can narrow at that same moment, and on the Match screen it routinely does: tapping your
   * own type offers all three, tapping an opponent's offers Stats alone (**D-9**). The state then
   * named a tab whose button was gone, and the body rendered it anyway — the code surface for a
   * colony whose document this screen deliberately withholds.
   *
   * Derived rather than corrected in an effect: an effect would render the wrong body for a frame
   * first, and would need its own guard against fighting the click handler. A value that cannot be
   * out of range has no invalid state to repair.
   */
  const activeTab: TypeSheetTab = offeredTabs.some((entry) => entry.id === tab)
    ? tab
    : (offeredTabs[0]?.id ?? 'code');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  /** Escape was pressed over an unsaved hand edit (UI/UX audit F-10): confirm rather than discard. */
  const [discardPrompted, setDiscardPrompted] = useState(false);

  // The sheet stays mounted while the player taps between chips (that is the point of it being a
  // popup), so a half-written draft must be abandoned when its subject changes — otherwise one
  // type's bytes would be saved over another's. The tab deliberately survives: a player comparing
  // two types' code should not be dropped back to Code each time.
  useEffect(() => {
    setEditing(false);
    setDraft('');
    setDiscardPrompted(false);
  }, [typeId]);

  // A turn starting mid-edit closes the editor rather than leaving a Save on screen whose
  // dispatch the server is guaranteed to refuse (the same rule `Colony.tsx`'s `ScriptBody` keeps).
  useEffect(() => {
    if (turnInFlight) {
      setEditing(false);
      setDraft('');
    }
  }, [turnInFlight]);

  /**
   * **Escape closes the sheet** (UI/UX audit **F-10**) — without making it modal (**F-09**).
   *
   * A **document-level** listener, not an `onKeyDown` on the sheet's root. The root handler was the
   * audit's first proposal and would not have worked: this sheet deliberately does not autofocus, so
   * focus is *outside* it while it is open (measured `document.activeElement === body`; in mouse use
   * it is the bee-type chip that was clicked). A keydown from either target never bubbles through the
   * sheet, so the handler would never fire.
   *
   * Three rules make a document listener safe here:
   *
   *   * **Overlay precedence.** `ChatOverlay` renders above this sheet (z-index 50 vs 20) and owns
   *     Escape while it is open — where it means "stop the turn, then close" rather than a plain
   *     dismiss. Checked by presence, and by `defaultPrevented`, which its own handler already sets.
   *   * **The unsaved hand edit wins.** An in-progress edit is not discarded by a stray Escape; the
   *     first press leaves the sheet open and asks. Losing typed B++ to a keystroke would be a worse
   *     defect than the missing shortcut this fixes.
   *   * **Cleanup on unmount**, so a closed sheet cannot keep swallowing Escape.
   *
   * **A second Escape dismisses the prompt** rather than doing nothing (review follow-up). The first
   * version opened the prompt and then ignored Escape entirely, so a keyboard user who pressed it had
   * no way back out without a mouse — a dead end introduced by the very fix meant to add a keyboard
   * path. Escape is the *cancelling* key, so it maps to "Keep editing" (the safe branch), never to
   * discarding the draft; the destructive choice stays an explicit press of the button, which is
   * focused when the prompt opens.
   */
  useEffect(() => {
    if (onClose === undefined) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || event.defaultPrevented) {
        return;
      }
      // The chat overlay is above this sheet and owns Escape while it is up.
      if (document.querySelector('[data-testid="chat-overlay"]') !== null) {
        return;
      }
      if (discardPrompted) {
        // The prompt is up: Escape cancels *it*, and never the draft it is protecting.
        event.preventDefault();
        setDiscardPrompted(false);
        return;
      }
      if (editing && draft !== '') {
        event.preventDefault();
        setDiscardPrompted(true);
        return;
      }
      event.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, editing, draft, discardPrompted]);

  /** Focus **Keep editing** when the prompt opens, so the keyboard has somewhere to land and Tab
   * reaches Discard from there. Without this the prompt is announced but unreachable: the sheet does
   * not trap focus (deliberately — see the file header), so focus is outside it when Escape arrives. */
  useEffect(() => {
    if (!discardPrompted) {
      return;
    }
    document.querySelector<HTMLElement>('[data-testid="type-sheet-discard-keep"]')?.focus();
  }, [discardPrompted]);

  const degraded = !documentValid;
  const slice = degraded ? null : sliceType(colonyDocument, typeName);
  const editSource = degraded ? colonyDocument : (slice ?? '');

  const commit = degraded ? onSaveDocument : onSaveSlice;
  // Withheld during a turn; withheld too when there is nothing extractable to edit, since an
  // empty draft saved over a type would be a fabricated program.
  const editable = !turnInFlight && commit !== undefined && (degraded || slice !== null);

  const count = countForType(population, typeId);
  const total = population.available ? population.total : null;
  const share = count !== null && total !== null && total > 0 ? Math.round((count / total) * 100) : null;

  function beginEdit(): void {
    setDraft(editSource);
    setEditing(true);
  }

  function save(): void {
    // Optimistic return to read-only, mirroring `ScriptBody`: the outcome (a new document, or a
    // rejection carrying `editDiagnostics`) reconciles the view, not a synchronous result here.
    commit?.(draft);
    setEditing(false);
  }

  return (
    <div
      data-testid="type-sheet"
      role="dialog"
      aria-label={`${titleCase(typeName)} — bee type`}
      style={{
        position: 'fixed',
        top: TYPE_SHEET_INSET_PX,
        left: TYPE_SHEET_INSET_PX,
        right: TYPE_SHEET_INSET_PX,
        bottom: TYPE_SHEET_STACK_CLEARANCE_PX,
        zIndex: 20,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'var(--surface-app)',
        border: '1.5px solid var(--hud-line)',
        borderRadius: 'var(--r-lg)',
        boxShadow: 'var(--shadow-sheet)',
        ...style,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px 8px' }}>
        {sprite !== undefined && (
          <img
            src={sprite}
            alt={typeName}
            style={{ width: 30, height: 30, imageRendering: 'pixelated', flex: 'none' }}
          />
        )}
        <h3
          data-testid="type-sheet-title"
          style={{ margin: 0, fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 19, color: 'var(--ink-1)' }}
        >
          {titleCase(typeName)}
        </h3>
        <span
          data-testid="type-sheet-census"
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: 13,
            color: 'var(--text-muted)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {count === null ? ABSENT : count.toLocaleString()} at {playheadLabel ?? ABSENT_CLOCK}
        </span>
        {headerRemainder}
        {onClose !== undefined && (
          <button
            data-testid="type-sheet-close"
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{
              marginLeft: 'auto',
              width: 'var(--hit-target)',
              height: 'var(--hit-target)',
              flex: 'none',
              borderRadius: 'var(--r-md)',
              border: '1.5px solid var(--line-strong)',
              background: 'var(--surface-card)',
              color: 'var(--ink-2)',
              fontSize: 15,
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, padding: '0 12px 8px', flexWrap: 'wrap', alignItems: 'center' }}>
        {buff != null && buff.length > 0 && (
          <span
            data-testid="type-sheet-buff"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              minHeight: 30,
              padding: '0 11px',
              borderRadius: 'var(--r-pill)',
              border: '1px solid var(--honey-300)',
              background: 'var(--accent-soft)',
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              fontSize: 11,
              // On a honey tint, not on cream (UI/UX audit F-06): --accent-strong is 4.49:1 there.
              color: 'var(--accent-on-soft)',
            }}
          >
            {buff} ×{buffMultiplier}
          </span>
        )}
        {onSetBirthType === undefined ? (
          <span
            data-testid="type-sheet-birth-type"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              minHeight: 30,
              padding: '0 11px',
              borderRadius: 'var(--r-pill)',
              border: '1px solid var(--line-strong)',
              background: birthType ? 'var(--accent-soft)' : 'var(--surface-card)',
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              fontSize: 11,
              color: birthType ? 'var(--accent-on-soft)' : 'var(--ink-2)',
            }}
          >
            {birthType ? '★' : '☆'} Birth type
          </span>
        ) : (
          <button
            data-testid="type-sheet-birth-type"
            type="button"
            aria-pressed={birthType}
            onClick={onSetBirthType}
            style={{
              ...CONTROL_BASE,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '0 14px',
              fontWeight: 600,
              fontSize: 11,
              border: `1px solid ${birthType ? 'var(--honey-300)' : 'var(--line-strong)'}`,
              background: birthType ? 'var(--accent-soft)' : 'var(--surface-card)',
              color: birthType ? 'var(--accent-on-soft)' : 'var(--ink-2)',
            }}
          >
            {birthType ? '★' : '☆'} Birth type
          </button>
        )}
      </div>

      {toolbar !== undefined && (
        <div data-testid="type-sheet-toolbar" style={{ padding: '0 12px 8px' }}>
          {toolbar}
        </div>
      )}

      {/* **UI/UX audit F-10.** Escape over an unsaved hand edit asks rather than discards. Rendered
          in the sheet's own flow — not as a nested dialog — because this sheet is deliberately
          non-modal (**F-09**) and stacking a modal inside it would take that back. */}
      {discardPrompted && (
        <div
          data-testid="type-sheet-discard-prompt"
          role="alertdialog"
          aria-label="Discard this edit?"
          style={{
            flex: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            margin: '0 var(--sp-3) var(--sp-2)',
            padding: '8px 10px',
            borderRadius: 'var(--r-md)',
            background: 'var(--honey-100)',
            border: '1px solid var(--honey-300)',
            fontFamily: 'var(--font-body)',
            fontSize: 12,
            color: 'var(--ink-1)',
          }}
        >
          <span style={{ flex: 1, minWidth: 0 }}>You have an unsaved edit to this type.</span>
          <button
            type="button"
            data-testid="type-sheet-discard-keep"
            onClick={() => setDiscardPrompted(false)}
            style={{
              flex: 'none',
              minHeight: 'var(--hit-target)',
              padding: '0 12px',
              borderRadius: 'var(--r-pill)',
              border: '1.5px solid var(--line-strong)',
              background: 'var(--surface-card)',
              color: 'var(--ink-1)',
              fontFamily: 'var(--font-display)',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Keep editing
          </button>
          <button
            type="button"
            data-testid="type-sheet-discard-confirm"
            onClick={() => {
              setDiscardPrompted(false);
              setEditing(false);
              setDraft('');
              onClose?.();
            }}
            style={{
              flex: 'none',
              minHeight: 'var(--hit-target)',
              padding: '0 12px',
              borderRadius: 'var(--r-pill)',
              border: '1.5px solid var(--danger)',
              background: 'var(--danger-soft)',
              color: 'var(--danger)',
              fontFamily: 'var(--font-display)',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Discard and close
          </button>
        </div>
      )}

      <div
        role="tablist"
        aria-label="Bee type view"
        style={{
          display: 'flex',
          gap: 4,
          margin: '0 12px 10px',
          padding: 4,
          borderRadius: 'var(--r-pill)',
          background: 'var(--paper-1)',
        }}
      >
        {offeredTabs.map((t) => (
          <button
            key={t.id}
            data-testid={`type-sheet-tab-${t.id}`}
            type="button"
            role="tab"
            aria-selected={activeTab === t.id}
            onClick={() => setTab(t.id)}
            style={{
              ...CONTROL_BASE,
              flex: 1,
              padding: '0 12px',
              border: 'none',
              background: tab === t.id ? 'var(--surface-card)' : 'transparent',
              color: tab === t.id ? 'var(--ink-1)' : 'var(--ink-3)',
              boxShadow: tab === t.id ? 'var(--shadow-card)' : 'none',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        style={{
          flex: 1,
          minHeight: 0,
          margin: '0 12px 10px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {activeTab === 'code' && (
          <div
            data-testid="type-sheet-code"
            style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}
          >
            {degraded && (
              <div
                data-testid="type-sheet-degraded-banner"
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  background: 'var(--danger-soft)',
                  border: '1.5px solid var(--danger)',
                  borderRadius: 'var(--r-md)',
                  padding: '10px 12px',
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: '#7E2F1D',
                }}
              >
                <span aria-hidden="true">✕</span>
                <div>
                  <b style={{ fontFamily: 'var(--font-display)', fontWeight: 600, color: 'var(--danger)' }}>
                    Can&rsquo;t read your scripts.
                  </b>{' '}
                  Showing the whole file so you can fix it
                  {documentDiagnostics?.[0]
                    ? ` — line ${documentDiagnostics[0].line}: ${documentDiagnostics[0].message}`
                    : '.'}
                </div>
              </div>
            )}
            <span
              data-testid={degraded ? 'type-sheet-degraded-scope' : 'type-sheet-slice-scope'}
              style={{
                display: 'inline-flex',
                alignSelf: 'flex-start',
                alignItems: 'center',
                gap: 4,
                fontFamily: 'var(--font-body)',
                fontSize: 11,
                color: 'var(--ink-2)',
                background: 'var(--paper-1)',
                border: '1px dashed var(--line-strong)',
                borderRadius: 'var(--r-pill)',
                padding: '3px 10px',
              }}
            >
              {degraded ? (
                '▸ showing whole document'
              ) : (
                <>
                  ▸ <span style={{ fontFamily: 'var(--font-mono)' }}>{typeName}</span> slice
                </>
              )}
            </span>

            {editing ? (
              <>
                <textarea
                  data-testid="type-sheet-edit-draft"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  spellCheck={false}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    minHeight: 180,
                    resize: 'vertical',
                    background: 'var(--hud-bg-solid)',
                    color: '#E8DFC8',
                    border: `1.5px solid ${
                      editDiagnostics && editDiagnostics.length > 0 ? 'var(--danger)' : 'var(--accent)'
                    }`,
                    borderRadius: 'var(--r-md)',
                    padding: 'var(--sp-4)',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 12,
                    lineHeight: 1.65,
                  }}
                />
                {editDiagnostics && editDiagnostics.length > 0 && (
                  <div
                    data-testid="type-sheet-edit-diagnostics"
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 11,
                      color: 'var(--danger)',
                      background: 'var(--danger-soft)',
                      borderRadius: 'var(--r-md)',
                      padding: '8px 10px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                    }}
                  >
                    {editDiagnostics.map((d, i) => (
                      <div key={i}>{d}</div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ flex: 1, fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-muted)' }}>
                    {degraded
                      ? 'Repairing the whole document — every type at once.'
                      : 'Editing the document’s own bytes — comments included.'}
                  </span>
                  <button
                    data-testid="type-sheet-edit-cancel"
                    type="button"
                    onClick={() => setEditing(false)}
                    style={{
                      ...CONTROL_BASE,
                      background: 'var(--surface-card)',
                      color: 'var(--ink-2)',
                      border: '1px solid var(--line-strong)',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    data-testid="type-sheet-edit-save"
                    type="button"
                    onClick={save}
                    style={{
                      ...CONTROL_BASE,
                      padding: '0 20px',
                      background: 'var(--accent-fill)',
                      color: '#FFFDF6',
                      border: '1px solid var(--accent-strong)',
                    }}
                  >
                    Save edits
                  </button>
                </div>
              </>
            ) : (
              <>
                {editable && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      data-testid="type-sheet-edit"
                      type="button"
                      onClick={beginEdit}
                      style={{
                        ...CONTROL_BASE,
                        fontWeight: 600,
                        fontSize: 12,
                        border: '1px solid var(--line-strong)',
                        background: 'var(--surface-card)',
                        color: 'var(--accent-strong)',
                      }}
                    >
                      ✎ Edit
                    </button>
                  </div>
                )}
                {degraded ? (
                  // The whole file with the offending location marked — the same treatment the
                  // live screen's degraded view uses, so a player meets one repair surface.
                  <pre
                    style={{
                      margin: 0,
                      background: 'var(--hud-bg-solid)',
                      color: '#E8DFC8',
                      borderRadius: 'var(--r-md)',
                      padding: 'var(--sp-4)',
                      overflowX: 'auto',
                      fontFamily: 'var(--font-mono)',
                      fontSize: 12,
                      lineHeight: 1.65,
                    }}
                  >
                    {documentLines(colonyDocument, documentDiagnostics?.[0]?.line).map((line) => (
                      <div
                        key={line.number}
                        data-testid={line.offending ? 'type-sheet-degraded-offending-line' : undefined}
                        style={
                          line.offending
                            ? {
                                background: 'rgba(196,71,46,.28)',
                                margin: '0 calc(-1 * var(--sp-4))',
                                padding: '0 var(--sp-4)',
                              }
                            : undefined
                        }
                      >
                        {line.text.length === 0 ? ' ' : line.text}
                      </div>
                    ))}
                  </pre>
                ) : slice === null ? (
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-muted)' }}>
                    This type has no block in the document yet.
                  </div>
                ) : (
                  <CodeView code={slice} style={{ fontSize: 12 }} />
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'flowchart' && (
          <div
            data-testid="type-sheet-flowchart"
            style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}
          >
            {degraded || slice === null ? (
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-muted)' }}>
                {/* UI/UX audit F-01: this must never again assert something false about the
                    player's program. Two genuinely different situations, said apart. */}
                {degraded
                  ? 'The document doesn’t parse, so no diagram can be built. Fix the errors in Code and this will fill in.'
                  : slice === null
                    ? 'This type has no block in the document yet.'
                    : 'Diagram unavailable'}
              </div>
            ) : (
              <BppProgramDiagram source={slice} label={`${typeName} B++ program diagram`} color={color} />
            )}
          </div>
        )}

        {activeTab === 'stats' && (
          <div
            data-testid="type-sheet-statistics"
            style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}
          >
            <div style={{ display: 'flex', gap: 8 }}>
              <Stat
                stat="alive"
                label="Alive now"
                value={count === null ? ABSENT : count.toLocaleString()}
              />
              <Stat stat="share" label="Share of colony" value={share === null ? ABSENT : `${share}%`} />
            </div>
            <p
              data-testid="type-sheet-statistics-note"
              style={{
                margin: 0,
                background: 'var(--info-soft)',
                border: '1px solid var(--line)',
                borderRadius: 'var(--r-md)',
                padding: '8px 10px',
                fontFamily: 'var(--font-body)',
                fontSize: 11,
                lineHeight: 1.45,
                color: 'var(--ink-2)',
              }}
            >
              {STATS_GAP_NOTE}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/** One sourced figure. `data-stat` is the hook that makes "exactly these figures, and no others"
 * assertable — a fabricated stat would have to add a card here to render. */
function Stat({ stat, label, value }: { stat: string; label: string; value: string }) {
  return (
    <div
      data-stat={stat}
      data-testid={`type-sheet-stat-${stat}`}
      style={{
        flex: 1,
        background: 'var(--surface-card)',
        border: '1.5px solid var(--line)',
        borderRadius: 'var(--r-md)',
        padding: '8px 10px',
        boxShadow: 'var(--shadow-card)',
      }}
    >
      <span
        style={{
          display: 'block',
          fontFamily: 'var(--font-body)',
          fontWeight: 800,
          fontSize: 9,
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps)',
          color: 'var(--text-muted)',
          marginBottom: 2,
        }}
      >
        {label}
      </span>
      <b
        data-testid={`type-sheet-stat-${stat}-value`}
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 600,
          fontSize: 20,
          color: 'var(--ink-1)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </b>
    </div>
  );
}
